import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, CircularProgress, Grid, Typography, useTheme, withStyles} from "@material-ui/core";
import {Skeleton} from "@material-ui/lab";
import classnames from 'classnames';
import {FormattedMessage, injectIntl, useIntl} from "react-intl";
import formatMoney from "../../../utils/formatMoney";
import TextField from "../../../components/TextField";
import CountrySelect from "../../../components/CountrySelect";
import RegionInput from "../../../components/RegionInput";
import Errors from "../../../utils/Errors";
import validateBillingAddress from "../../../utils/validateBillingAddress";

const styles = (theme) => ({
  container: {
    marginTop: theme.spacing(0.5)
  },
  formControl: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'row',
    height: 40,
  },
  cardField: {
    width: '100%',
    height: 50,
    marginTop: theme.spacing(0.5)
  },
  label: {
    paddingBottom: theme.spacing(1)
  },
  skeletonCardField: {
    width: '100%',
    height: 50,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(0.5),
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '16px',
    paddingBottom: '16px',
  },
  hidden: {
    display: 'none'
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  error: {
    color: theme.palette.error.main
  },
  cloverFooter: {
    padding: '18px 24px',
    borderRadius: '0 0 14px 14px',
    textAlign: 'center'
  },
  cloverLockImage: {
    top: 2,
    position: 'relative',
    marginRight: theme.spacing(1)
  },
  cloverLogo: {
    maxWidth: 20,
    position: 'relative',
    top: 4,
    marginLeft: theme.spacing(1)
  },
  cloverPowered: {
    color: theme.palette.text.disabled,
    fontWeight: 'bold'
  },
  cloverPrivacyPolicy: {
    color: theme.palette.text.disabled,
  }
});

CloverCardDetailsField.propTypes = {
  cloverMerchantId: PropTypes.string.isRequired,
  apiKey: PropTypes.string.isRequired,
  initializingPaymentConfig: PropTypes.bool.isRequired,
  showZip: PropTypes.bool,
  showBillingAddress: PropTypes.bool,
  defaultCountryCode: PropTypes.string,
  amountDue: PropTypes.object.isRequired,
  idempotencyKey: PropTypes.string.isRequired,
  onPaymentTokenCreated: PropTypes.func.isRequired,
  classes: PropTypes.object
};

function CloverCardDetailsField({
                                  cloverMerchantId,
                                  apiKey,
                                  initializingPaymentConfig,
                                  showZip = true,
                                  showBillingAddress = true,
                                  defaultCountryCode = 'US',
                                  amountDue,
                                  idempotencyKey,
                                  onPaymentTokenCreated,
                                  classes,
                                }) {

  const [initialized, setInitialized] = useState(false);
  const [cloverSdk, setCloverSdk] = useState(null);
  const [useBillingAddress, setUseBillingAddress] = useState(showBillingAddress);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState(null);
  const [region, setRegion] = useState(null);
  const cardNumberInputRef = useRef(null);
  const cardNameInputRef = useRef(null);
  const cardExpiryInputRef = useRef(null);
  const cardCvcInputRef = useRef(null);
  const cardZipInputRef = useRef(null);
  const cardStreetAddressInputRef = useRef(null);

  const [cardNumberError, setCardNumberError] = useState(null);
  const [cardNumberValid, setCardNumberValid] = useState(false);
  const [cardNameError, setCardNameError] = useState(null);
  const [cardNameValid, setCardNameValid] = useState(false);
  const [cardExpiryError, setCardExpiryError] = useState(null);
  const [cardExpiryValid, setCardExpiryValid] = useState(false);
  const [cardCvcError, setCardCvcError] = useState(null);
  const [cardCvcValid, setCardCvcValid] = useState(false);
  const [cardZipError, setCardZipError] = useState(null);
  const [cardZipValid, setCardZipValid] = useState(false);
  const [cardStreetAddressError, setCardStreetAddressError] = useState(null);
  const [cardStreetAddressValid, setCardStreetAddressValid] = useState(false);
  const [billingAddressErrors, setBillingAddressErrors] = useState(new Errors());
  const [paying, setPaying] = useState(false);
  const [idempotencyPaymentTokenMap, setIdempotencyPaymentTokenMap] = useState(new Map());

  const theme = useTheme();
  const intl = useIntl();

  const cloverBaseInputStyle = {
    'font-size': theme.typography.body2.fontSize,
    'font-family': theme.typography.body2.fontFamily,
    'height': '100%',
    'borderRadius': theme.shape.borderRadius,
    'borderColor': 'rgba(0, 0, 0, 0.23)',
    'borderWidth': '1px',
    'borderStyle': 'solid',
    'paddingLeft': '16px',
    'paddingRight': '16px',
    'paddingTop': '16px',
    'paddingBottom': '16px',
  };

  const cloverInputFocusStyle = {
    'borderColor': theme.palette.primary.main,
    'borderWidth': '2px',
    'paddingTop': '15px',
    'paddingBottom': '15px',
    'paddingLeft': '15px',
    'paddingRight': '15px',
  };

  const cloverInputHoverStyle = {
    'borderColor': theme.palette.text.primary
  };

  const cloverCardNumberImageStyle = {
    'max-height': '20px',
    'right': '8px',
    'top': '14.5px'
  };

  const cloverCardNumberStyle = {
    'card-number input': cloverBaseInputStyle,
    'card-number input:hover': cloverInputHoverStyle,
    'card-number input:focus': cloverInputFocusStyle,
    'card-number .brand': cloverCardNumberImageStyle,
  };

  const cloverCardNameStyle = {
    'card-name input': cloverBaseInputStyle,
    'card-name input:hover': cloverInputHoverStyle,
    'card-name input:focus': cloverInputFocusStyle,
  };

  const cloverCardExpiryStyle = {
    'card-date input': cloverBaseInputStyle,
    'card-date input:hover': cloverInputHoverStyle,
    'card-date input:focus': cloverInputFocusStyle,
  };

  const cloverCardCvcStyle = {
    'card-cvv input': cloverBaseInputStyle,
    'card-cvv input:hover': cloverInputHoverStyle,
    'card-cvv input:focus': cloverInputFocusStyle,
  };

  const cloverCardZipStyle = {
    'card-postal-code input': cloverBaseInputStyle,
    'card-postal-code input:hover': cloverInputHoverStyle,
    'card-postal-code input:focus': cloverInputFocusStyle,
  };

  const cloverCardStreetAddressStyle = {
    'card-street input': cloverBaseInputStyle,
    'card-street input:hover': cloverInputHoverStyle,
    'card-street input:focus': cloverInputFocusStyle,
  };

  useEffect(() => {
    if (apiKey && !initializingPaymentConfig && !initialized) {
      const clover = new window.Clover(apiKey, {
        merchantId: cloverMerchantId
      });

      let cloverElements = clover.elements();
      const cloverCardNumber = cloverElements.create('CARD_NUMBER', cloverCardNumberStyle);
      const cloverCardName = cloverElements.create('CARD_NAME', cloverCardNameStyle);
      const cloverCardExpiry = cloverElements.create('CARD_DATE', cloverCardExpiryStyle);
      const cloverCardCvc = cloverElements.create('CARD_CVV', cloverCardCvcStyle);
      const cloverCardZip = cloverElements.create('CARD_POSTAL_CODE', cloverCardZipStyle);
      const cloverCardStreetAddress = cloverElements.create('CARD_STREET_ADDRESS', cloverCardStreetAddressStyle);
      cloverCardNumber.mount('#clover-card-number');
      cloverCardName.mount('#clover-card-name');
      cloverCardExpiry.mount('#clover-card-expiry');
      cloverCardCvc.mount('#clover-card-cvc');
      cloverCardZip.mount('#clover-card-zip');
      cloverCardStreetAddress.mount('#clover-card-street-address');

      cloverCardNumber.addEventListener('change', (e) => {
        if (e['CARD_NUMBER'].touched && !e['CARD_NUMBER'].error) {
          setCardNumberError(null);
          setCardNumberValid(true);
        }
      });

      cloverCardNumber.addEventListener('blur', (e) => {
        if (e['CARD_NUMBER'].touched && e['CARD_NUMBER'].error) {
          setCardNumberError(e['CARD_NUMBER'].error);
          setCardNumberValid(false);
        }
      });

      cloverCardName.addEventListener('change', (e) => {
        if (e['CARD_NAME'].touched && !e['CARD_NAME'].error) {
          setCardNameError(null);
          setCardNameValid(true);
        }
      });

      cloverCardName.addEventListener('blur', (e) => {
        if (e['CARD_NAME'].touched && e['CARD_NAME'].error) {
          setCardNameError(e['CARD_NAME'].error);
          setCardNameValid(false);
        }
      });

      cloverCardExpiry.addEventListener('change', (e) => {
        if (e['CARD_DATE'].touched && !e['CARD_DATE'].error) {
          setCardExpiryError(null);
          setCardExpiryValid(true);
        }
      });

      cloverCardExpiry.addEventListener('blur', (e) => {
        if (e['CARD_DATE'].touched && e['CARD_DATE'].error) {
          setCardExpiryError(e['CARD_DATE'].error);
          setCardExpiryValid(false);
        }
      });

      cloverCardCvc.addEventListener('change', (e) => {
        if (e['CARD_CVV'].touched && !e['CARD_CVV'].error) {
          setCardCvcError(null);
          setCardCvcValid(true);
        }
      });

      cloverCardCvc.addEventListener('blur', (e) => {
        if (e['CARD_CVV'].touched && e['CARD_CVV'].error) {
          setCardCvcError(e['CARD_CVV'].error);
          setCardCvcValid(false);
        }
      });

      cloverCardZip.addEventListener('change', (e) => {
        if (e['CARD_POSTAL_CODE'].touched && !e['CARD_POSTAL_CODE'].error) {
          setCardZipError(null);
          setCardZipValid(true);
        }
      });

      cloverCardZip.addEventListener('blur', (e) => {
        if (e['CARD_POSTAL_CODE'].touched && e['CARD_POSTAL_CODE'].error) {
          setCardZipError(e['CARD_POSTAL_CODE'].error);
          setCardZipValid(false);
        }
      });

      cloverCardStreetAddress.addEventListener('change', (e) => {
        if (e['CARD_STREET_ADDRESS'].touched && !e['CARD_STREET_ADDRESS'].error) {
          setCardStreetAddressError(null);
          setCardStreetAddressValid(true);
        }
      });

      cloverCardStreetAddress.addEventListener('blur', (e) => {
        if (e['CARD_STREET_ADDRESS'].touched && e['CARD_STREET_ADDRESS'].error) {
          if(e['CARD_STREET_ADDRESS'].error === 'errors.streetAddrRequired') {
            setCardStreetAddressError('Street address is required');
          } else {
            setCardStreetAddressError(e['CARD_STREET_ADDRESS'].error);
          }
          setCardStreetAddressValid(false);
        }
      });

      const cardNumberIFrame = cardNumberInputRef.current.children[0];
      cardNumberIFrame.width = '100%';
      cardNumberIFrame.height = '50px';
      cardNumberIFrame.style.width = '100%';
      cardNumberIFrame.style.height = '50px';
      cardNumberIFrame.style.marginRight = '4px';
      cardNumberIFrame.addEventListener('load', () => {
        setInitialized(true);
      });

      const cardNameIFrame = cardNameInputRef.current.children[0];
      cardNameIFrame.width = '100%';
      cardNameIFrame.height = '50px';
      cardNameIFrame.style.width = '100%';
      cardNameIFrame.style.height = '50px';
      cardNameIFrame.style.marginRight = '4px';
      cardNameIFrame.addEventListener('load', () => {
        setInitialized(true);
      });

      const cardExpiryIFrame = cardExpiryInputRef.current.children[0];
      cardExpiryIFrame.width = '100%';
      cardExpiryIFrame.height = '50px';
      cardExpiryIFrame.style.width = '100%';
      cardExpiryIFrame.style.height = '50px';
      cardExpiryIFrame.addEventListener('load', () => {
        setInitialized(true);
      });

      const cardCvcIFrame = cardCvcInputRef.current.children[0];
      cardCvcIFrame.width = '100%';
      cardCvcIFrame.height = '50px';
      cardCvcIFrame.style.width = '100%';
      cardCvcIFrame.style.height = '50px';
      cardCvcIFrame.addEventListener('load', () => {
        setInitialized(true);
      });

      const cardZipIFrame = cardZipInputRef.current.children[0];
      cardZipIFrame.width = '100%';
      cardZipIFrame.height = '50px';
      cardZipIFrame.style.width = '100%';
      cardZipIFrame.style.height = '50px';
      cardZipIFrame.addEventListener('load', () => {
        setInitialized(true);
      });

      const cardStreetAddressIFrame = cardStreetAddressInputRef.current.children[0];
      cardStreetAddressIFrame.width = '100%';
      cardStreetAddressIFrame.height = '50px';
      cardStreetAddressIFrame.style.width = '100%';
      cardStreetAddressIFrame.style.height = '50px';
      cardStreetAddressIFrame.addEventListener('load', () => {
        setInitialized(true);
      });

      setCloverSdk(clover);

    }
  }, [apiKey, initializingPaymentConfig]);

  // Add mutation observer to detect the Clover footer injected by the Clover SDK
  useEffect(() => {
    const config = {childList: true};
    const mutationObserver = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          // Check if the Clover footer was attached
          let nodes = document.body.getElementsByClassName('clover-footer');
          if (nodes.length > 0) {
            // Remove clover footer from document
            document.body.removeChild(nodes.item(0));
            observer.disconnect();
          }
        }
      }
    });
    mutationObserver.observe(document.body, config);
  }, []);

  function handleOnPayClick() {
    if (validate()) {
      setPaying(true);
      // Check if a payment token already was created for the given idempotency key
      // If yes, return the created payment token
      if (idempotencyPaymentTokenMap.get(idempotencyKey)) {
        onPaymentTokenCreated(idempotencyPaymentTokenMap.get(idempotencyKey))
          .then(() => {
            setPaying(false);
          })
          .catch((errorMessage) => {
            setPaying(false);
          });
      } else {
        cloverSdk.createToken()
          .then((result) => {
            if (result.errors) {
              setPaying(false);
            } else {
              const paymentToken = {
                  paymentProvider: 'CLOVER',
                  type: 'SOURCE',
                  sourceId: result.token,
                  cloverMerchantId,
                  billingAddress: {
                    firstName,
                    lastName,
                    street1: addressLine1,
                    street2: addressLine2,
                    city,
                    postalcode: postalCode,
                    region,
                    country: countryCode
                  }
              };

              idempotencyPaymentTokenMap.set(idempotencyKey, paymentToken);
              setIdempotencyPaymentTokenMap(idempotencyPaymentTokenMap);
              onPaymentTokenCreated(paymentToken)
                .then(() => {
                  setPaying(false);
                })
                .catch((errorMessage) => {
                  setPaying(false);
                });
            }
          });
      }

    }
  }

  function handleUpdateFirstname(firstName) {
    setBillingAddressErrors(billingAddressErrors.clear('firstName'));
    setFirstName(firstName);
  }

  function handleUpdateLastname(lastName) {
    setBillingAddressErrors(billingAddressErrors.clear('lastName'));
    setLastName(lastName);
  }

  function handleUpdateStreet1(street1) {
    setBillingAddressErrors(billingAddressErrors.clear('addressLine1'));
    setAddressLine1(street1);
  }

  function handleUpdatePostalCode(postalCode) {
    setBillingAddressErrors(billingAddressErrors.clear('postalCode'));
    setPostalCode(postalCode);
  }

  function handleUpdateCity(city) {
    setBillingAddressErrors(billingAddressErrors.clear('city'));
    setCity(city);
  }

  function handleUpdateRegion(region) {
    setBillingAddressErrors(billingAddressErrors.clear('region'));
    setRegion(region);
  }

  function validate() {
    let valid = true;
    if (!cardNumberValid) {
      valid = false;
      if (cardNumberError === null) {
        setCardNumberError('Card number is required.');
      }

    }
    if (!cardExpiryValid) {
      valid = false;
      if (cardExpiryError === null) {
        setCardExpiryError('Expiration is required.');
      }
    }
    if (!cardCvcValid) {
      valid = false;
      if (cardCvcError === null) {
        setCardCvcError('CVV is required.');
      }
    }
    if (!cardZipValid) {
      valid = false;
      if (cardZipError === null) {
        setCardZipError('Postal code is required.');
      }
    }

    const errors = validateBillingAddress(firstName, lastName, addressLine1, city, postalCode, region, countryCode);
    setBillingAddressErrors(errors);
    if(errors.hasErrors()) {
      valid = false;
    }
    return valid;
  }

  const cardFieldClass = initialized ? classes.cardField : classnames(classes.cardField, classes.hidden);

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid item xs={12}>
        <Typography variant={'button'} className={classes.label}>
          <FormattedMessage id={'label.billing-details'}/>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {!initialized && <Skeleton variant="rect" className={classes.skeletonCardField}/>}
        <div id={'clover-card-name'} ref={cardNameInputRef} className={cardFieldClass}/>
        {cardNumberError !== null &&
          <Typography variant={'caption'} className={classes.error}>
            {cardNameError}
          </Typography>
        }
      </Grid>
      <Grid item xs={12}>
        {!initialized && <Skeleton variant="rect" className={classes.skeletonCardField}/>}
        <div id={'clover-card-number'} ref={cardNumberInputRef} className={cardFieldClass}/>
        {cardNumberError !== null &&
        <Typography variant={'caption'} className={classes.error}>
          {cardNumberError}
        </Typography>
        }
      </Grid>
      <Grid item xs={4}>
        {!initialized && <Skeleton variant="rect" className={classes.skeletonCardField}/>}
        <div id={'clover-card-expiry'} ref={cardExpiryInputRef} className={cardFieldClass}/>
        {cardExpiryError !== null &&
        <Typography variant={'caption'} className={classes.error}>
          {cardExpiryError}
        </Typography>
        }
      </Grid>
      <Grid item xs={4}>
        {!initialized && <Skeleton variant="rect" className={classes.skeletonCardField}/>}
        <div id={'clover-card-cvc'} ref={cardCvcInputRef} className={cardFieldClass}/>
        {cardCvcError !== null &&
        <Typography variant={'caption'} className={classes.error}>
          {cardCvcError}
        </Typography>
        }
      </Grid>
      <Grid item xs={4}>
        {!initialized && <Skeleton variant="rect" className={classes.skeletonCardField}/>}
        <div id={'clover-card-zip'} ref={cardZipInputRef} className={cardFieldClass}/>
        {cardZipError !== null &&
        <Typography variant={'caption'} className={classes.error}>
          {cardZipError}
        </Typography>
        }
      </Grid>
      <Grid item xs={12}>
        {!initialized && <Skeleton variant="rect" className={classes.skeletonCardField}/>}
        <div id={'clover-card-street-address'} ref={cardStreetAddressInputRef} className={cardFieldClass}/>
        {cardStreetAddressError !== null &&
          <Typography variant={'caption'} className={classes.error}>
            {cardStreetAddressError}
          </Typography>
        }
      </Grid>
      {useBillingAddress &&
      <>
        <Grid item xs={12}>
          <Typography variant={'button'} className={classes.label}>
            <FormattedMessage id={'label.billing-address'}/>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={<FormattedMessage id={"placeholder.first-name"}/>}
            placeholder={intl.formatMessage({id: "placeholder.first-name"})}
            fullWidth={true}
            value={firstName}
            InputLabelProps={{
              shrink: false
            }}
            variant={'outlined'}
            onChange={(e) => handleUpdateFirstname(e.target.value)}
            data-test={'first-name'}
            error={billingAddressErrors.hasError('firstName')}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={<FormattedMessage id={"placeholder.last-name"}/>}
            placeholder={intl.formatMessage({id: "placeholder.last-name"})}
            fullWidth={true}
            value={lastName}
            InputLabelProps={{
              shrink: false
            }}
            variant={'outlined'}
            onChange={(e) => handleUpdateLastname(e.target.value)}
            data-test={'last-name'}
            error={billingAddressErrors.hasError('lastName')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={<FormattedMessage id={"placeholder.billing-address-street1"}/>}
            placeholder={intl.formatMessage({id: "placeholder.billing-address-street1"})}
            fullWidth={true}
            value={addressLine1}
            InputLabelProps={{
              shrink: false
            }}
            variant={'outlined'}
            onChange={(e) => handleUpdateStreet1(e.target.value)}
            data-test={'address-line1'}
            error={billingAddressErrors.hasError('addressLine1')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={<FormattedMessage id={"placeholder.billing-address-street2"}/>}
            placeholder={intl.formatMessage({id: "placeholder.billing-address-street2"})}
            fullWidth={true}
            value={addressLine2}
            InputLabelProps={{
              shrink: false
            }}
            variant={'outlined'}
            onChange={(e) => setAddressLine2(e.target.value)}
            data-test={'address-line2'}
            error={billingAddressErrors.hasError('addressLine2')}
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            label={<FormattedMessage id={"placeholder.billing-address-city"}/>}
            placeholder={intl.formatMessage({id: "placeholder.billing-address-city"})}
            fullWidth={true}
            value={city}
            InputLabelProps={{
              shrink: false
            }}
            variant={'outlined'}
            onChange={(e) => handleUpdateCity(e.target.value)}
            data-test={'city'}
            error={billingAddressErrors.hasError('city')}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label={<FormattedMessage id={"placeholder.billing-address-postalcode"}/>}
            placeholder={intl.formatMessage({id: "placeholder.billing-address-postalcode"})}
            fullWidth={true}
            value={postalCode}
            InputLabelProps={{
              shrink: false
            }}
            inputProps={{
              autofill: 'postal-code'
            }}
            variant={'outlined'}
            onChange={(e) => handleUpdatePostalCode(e.target.value)}
            data-test={'postalCode'}
            error={billingAddressErrors.hasError('postalCode')}
          />
        </Grid>
        <Grid item xs={6}>
          <CountrySelect
            label={<FormattedMessage id={"placeholder.billing-address-country"}/>}
            fullWidth={true}
            value={countryCode}
            variant={'outlined'}
            onChange={(e) => setCountryCode(e.target.value)}
            data-test={'country'}
            error={billingAddressErrors.hasError('country')}

          />
        </Grid>
        <Grid item xs={6}>
          <RegionInput
            id={'billing-address-region'}
            countryCode={countryCode}
            value={region}
            fullWidth
            onChange={(e) => handleUpdateRegion(e.target.value)}
            error={billingAddressErrors.hasError('region')}
          />
        </Grid>
      </>
      }
      <Grid item xs={12}>
        <Button
          variant={'contained'}
          color={'primary'}
          fullWidth={true}
          size={'large'}
          data-test={'button-pay'}
          disabled={paying}
          onClick={handleOnPayClick}
        >
          {paying && <CircularProgress size={24} thickness={2}/>}
          {!paying && <FormattedMessage id={"button.pay"}
                                        values={{amount: formatMoney(amountDue.amount, amountDue.currencyCode)}}/>}
        </Button>
      </Grid>

      <Grid item xs={12} className={classes.center}>
        <Typography variant={'body2'} className={classes.cloverPowered}>
            <img className={classes.cloverLockImage}
                 alt={'clover lock icon'}
               src={'https://checkout.sandbox.dev.clover.com/assets/icons/lock.png'}/>
            Secure Payments Powered by Clover
            <img
              className={classes.cloverLogo}
              alt={'clover logo'}
              src={'https://checkout.sandbox.dev.clover.com/assets/icons/clover-symbol.png'}/>
        </Typography>
        <Typography variant={'body2'}>
          <a  className={classes.cloverPrivacyPolicy} title="Privacy Policy" href={'https://www.clover.com/privacy-policy'}>Privacy Policy</a>
        </Typography>
      </Grid>
    </Grid>
);
}

export default withStyles(styles)(CloverCardDetailsField);
